import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { BreakpointOverrides, useTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { isKeyOf } from '../utils/guards';

type Props<T> = { [key in keyof BreakpointOverrides]?: T };
/**
 * Responsive checker for the element breakpoint width and matching values.
 *
 * Depending on the element width, we get the ability to define internal breakpoints.
 * With this, we can modify UI or state accordingly.
 *
 * Usage:
 * const [ref, value] = useComponentResponsiveProps({
 *     xs: {name: "super small"},
 *     sm: {name: "a bit bigger"},
 *     lg: {name: "large"},
 * });
 *
 * `ref` should be bound to the element whose width will be taken into consideration.
 * According to the element width, `value` will hold matched value.
 *
 * Values are deep-merged from the smallest to the largest, so that can be used to extend values between themselves.
 * @param data
 */
export const useComponentResponsiveProps = <T = Record<string, unknown>, K extends HTMLElement = HTMLElement>(
  data: Props<T>
): [MutableRefObject<K | null>, T] => {
  const ref = useRef<K | null>(null);
  const [, setRecalculate] = useState(true);
  const {
    breakpoints: { values: breakpointValues },
  } = useTheme();

  const elementWidth = ref.current?.clientWidth || window.innerWidth;

  // If we change the ref, force the recalculation. This is also triggered on the initial bind.
  useEffect(() => {
    setRecalculate((value) => !value);
  }, []);

  // On every window resize, recalculate width.
  useEffect(() => {
    const handler = () => {
      setRecalculate((value) => !value);
    };
    window.addEventListener('resize', handler);

    return () => window.removeEventListener('resize', handler);
  }, []);

  const activeProps = useMemo(() => {
    let props = {} as T;

    // Go through breakpoints and check we are at a wider definition.
    for (const key in breakpointValues) {
      if (!isKeyOf(key, breakpointValues)) {
        continue;
      }

      const width: number = breakpointValues[key];
      if (elementWidth >= width) {
        props = deepmerge(props, data[key] || {});
      }
    }
    return props;
  }, [breakpointValues, data, elementWidth]);

  return [ref, activeProps];
};
